/**
 * Copyright 2026 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useMemo } from 'react';
import { X, Upload, AlertCircle, CheckCircle, Loader, ChevronDown, ChevronRight, Download, Cloud } from 'lucide-react';
import type { UserStory, Epic } from '../../types';
import { useApp } from '../../contexts/AppContext';
import { SkillShopService } from '../../services/skillShopService';
import { exportToJiraCsv, type CsvEpic, type CsvUserStory } from '../../utils/jiraCsvExport';

type ExportFormat = 'api' | 'csv';

interface JiraUserStoryExportDialogProps {
  epics: Epic[];
  stories: UserStory[];
  onClose: () => void;
  onExportComplete: () => void;
}

type ExportResult = {
  successful: Array<{ item: any; key: string; type: 'epic' | 'story'; action: 'created' | 'updated' }>;
  failed: Array<{ item: any; error: string; type: 'epic' | 'story' }>;
};

/**
 * Dialog for exporting user stories and epics to JIRA.
 * Uses the existing backend implementation at /api/jira/user-stories/export.
 */
export function JiraUserStoryExportDialog({ epics, stories, onClose, onExportComplete }: JiraUserStoryExportDialogProps) {
  const { jiraConfig, isConfigured } = useApp();
  
  const [step, setStep] = useState<'select' | 'format' | 'config' | 'exporting' | 'complete'>('select');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');
  const [selectedEpics, setSelectedEpics] = useState<Set<string>>(new Set());
  const [selectedStories, setSelectedStories] = useState<Set<string>>(new Set());
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);
  const [csvExportResult, setCsvExportResult] = useState<{ epicCount: number; storyCount: number; filename: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skillShopService] = useState(() => new SkillShopService());
  
  // Use config from context or allow override
  const [config, setConfig] = useState({
    url: jiraConfig?.url || '',
    username: jiraConfig?.username || '',
    apiToken: jiraConfig?.apiToken || '',
    projectKey: jiraConfig?.projectKey || 'PROJ',
    issueType: jiraConfig?.issueType || 'Story',
    epicIssueType: jiraConfig?.epicIssueType || 'Epic',
    csvFilenamePrefix: 'velocity-export',
    bulletStyle: 'markdown' as 'markdown' | 'wiki',
    normalizeUnicode: false,
    includePriority: false,
    defaultPriority: 'Medium',
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{ success: boolean; error?: string } | null>(null);

  // Group stories by epic
  const storiesByEpic = useMemo(() => {
    const grouped: Record<string, UserStory[]> = {};
    stories.forEach(story => {
      const epicId = story.epicId || '__no_epic__';
      if (!grouped[epicId]) {
        grouped[epicId] = [];
      }
      grouped[epicId].push(story);
    });
    return grouped;
  }, [stories]);

  const toggleEpicSelection = (epicId: string) => {
    const newSelected = new Set(selectedEpics);
    const epicStories = storiesByEpic[epicId] || [];
    
    if (newSelected.has(epicId)) {
      newSelected.delete(epicId);
      // Deselect all stories in this epic
      epicStories.forEach(s => selectedStories.delete(s.id));
      setSelectedStories(new Set(selectedStories));
    } else {
      newSelected.add(epicId);
      // Select all stories in this epic
      const newStorySelection = new Set(selectedStories);
      epicStories.forEach(s => newStorySelection.add(s.id));
      setSelectedStories(newStorySelection);
    }
    setSelectedEpics(newSelected);
  };

  const toggleStorySelection = (storyId: string) => {
    const newSelected = new Set(selectedStories);
    if (newSelected.has(storyId)) {
      newSelected.delete(storyId);
    } else {
      newSelected.add(storyId);
    }
    setSelectedStories(newSelected);
  };

  const toggleEpicExpansion = (epicId: string) => {
    const newExpanded = new Set(expandedEpics);
    if (newExpanded.has(epicId)) {
      newExpanded.delete(epicId);
    } else {
      newExpanded.add(epicId);
    }
    setExpandedEpics(newExpanded);
  };

  const selectAll = () => {
    setSelectedEpics(new Set(epics.map(e => e.id)));
    setSelectedStories(new Set(stories.map(s => s.id)));
  };

  const selectNone = () => {
    setSelectedEpics(new Set());
    setSelectedStories(new Set());
  };

  const selectedEpicsList = epics.filter(e => selectedEpics.has(e.id));
  const selectedStoriesList = stories.filter(s => selectedStories.has(s.id));
  const totalSelected = selectedEpics.size + selectedStories.size;

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    setError(null);

    const result = await skillShopService.testJiraUserStoryConnection(config);
    setConnectionStatus(result);
    setTestingConnection(false);
  };

  const performCsvExport = () => {
    if (totalSelected === 0) {
      setError('Please select at least one epic or story to export.');
      return;
    }

    setError(null);
    setStep('exporting');

    // Prepare epics with story counts
    const epicsForCsv = selectedEpicsList.map(epic => ({
      epic: {
        id: epic.id,
        title: epic.title,
        description: epic.description || '',
        status: epic.status,
      } as CsvEpic,
      storyCount: (storiesByEpic[epic.id] || []).filter(s => selectedStories.has(s.id)).length
    }));

    // Prepare stories with epic titles
    const storiesForCsv = selectedStoriesList.map(story => {
      const epic = epics.find(e => e.id === story.epicId);
      return {
        story: {
          id: story.id,
          title: story.title,
          role: story.role || '',
          goal: story.goal || '',
          benefit: story.benefit || '',
          acceptanceCriteria: story.acceptanceCriteria || [],
          epicId: story.epicId,
          status: story.status,
        } as CsvUserStory,
        epicTitle: epic && selectedEpics.has(epic.id) ? `${epic.id}: ${epic.title}` : undefined
      };
    });

    // Generate and download CSV using the utility
    const result = exportToJiraCsv(epicsForCsv, storiesForCsv, {
      projectKey: config.projectKey,
      epicIssueType: config.epicIssueType,
      storyIssueType: config.issueType,
      filenamePrefix: config.csvFilenamePrefix,
      bulletStyle: config.bulletStyle,
      normalizeUnicode: config.normalizeUnicode,
      includePriority: config.includePriority,
      defaultPriority: config.defaultPriority,
    });

    // Set result and complete
    setCsvExportResult(result);
    setStep('complete');
  };

  const performApiExport = async () => {
    if (totalSelected === 0) {
      setError('Please select at least one epic or story to export.');
      return;
    }

    setExporting(true);
    setError(null);
    setStep('exporting');

    // Format epics for export
    const epicsToExport = selectedEpicsList.map(epic => ({
      id: epic.id,
      title: epic.title,
      description: epic.description,
      userStoryIds: (storiesByEpic[epic.id] || [])
        .filter(s => selectedStories.has(s.id))
        .map(s => s.id),
      section: epic.section,
      status: epic.status
    }));

    // Format stories for export
    const storiesToExport = selectedStoriesList.map(story => ({
      id: story.id,
      title: story.title,
      role: story.role,
      goal: story.goal,
      benefit: story.benefit,
      acceptanceCriteria: story.acceptanceCriteria || [],
      section: story.section,
      epicId: story.epicId,
      status: story.status
    }));

    const result = await skillShopService.exportUserStoriesToJira(config, epicsToExport, storiesToExport);

    if (result.success && result.data) {
      setExportResult(result.data);
      setStep('complete');
    } else {
      setError(result.error || 'Export failed');
      setStep('config');
    }
    
    setExporting(false);
  };

  const performExport = () => {
    if (exportFormat === 'csv') {
      performCsvExport();
    } else {
      performApiExport();
    }
  };

  const handleComplete = () => {
    onExportComplete();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Export User Stories to JIRA
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Selection Step */}
        {step === 'select' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Select Items to Export</h3>
                <p className="text-sm text-gray-600">
                  {totalSelected} item{totalSelected !== 1 ? 's' : ''} selected
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                >
                  Select All
                </button>
                <button
                  onClick={selectNone}
                  className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  Select None
                </button>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              {epics.length === 0 && stories.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No user stories or epics to export
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {epics.map(epic => {
                    const epicStories = storiesByEpic[epic.id] || [];
                    const isExpanded = expandedEpics.has(epic.id);
                    
                    return (
                      <div key={epic.id}>
                        <div className="flex items-center gap-3 p-3 hover:bg-gray-50">
                          <button
                            onClick={() => toggleEpicExpansion(epic.id)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                          <input
                            type="checkbox"
                            checked={selectedEpics.has(epic.id)}
                            onChange={() => toggleEpicSelection(epic.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <span className="font-medium text-gray-900">{epic.id}</span>
                            <span className="text-gray-600 ml-2">{epic.title}</span>
                          </div>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            Epic • {epicStories.length} stories
                          </span>
                        </div>
                        
                        {isExpanded && epicStories.length > 0 && (
                          <div className="pl-12 bg-gray-50 border-t border-gray-100">
                            {epicStories.map(story => (
                              <div key={story.id} className="flex items-center gap-3 p-2 hover:bg-gray-100">
                                <input
                                  type="checkbox"
                                  checked={selectedStories.has(story.id)}
                                  onChange={() => toggleStorySelection(story.id)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-900">{story.id}</span>
                                  <span className="text-sm text-gray-600 ml-2">{story.title}</span>
                                </div>
                                <span className="text-xs text-gray-500">{story.status}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Stories without epic */}
                  {storiesByEpic['__no_epic__']?.map(story => (
                    <div key={story.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                      <div className="w-4" /> {/* Spacer */}
                      <input
                        type="checkbox"
                        checked={selectedStories.has(story.id)}
                        onChange={() => toggleStorySelection(story.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">{story.id}</span>
                        <span className="text-gray-600 ml-2">{story.title}</span>
                      </div>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        Story (no epic)
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep('format')}
                disabled={totalSelected === 0}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue ({totalSelected} selected)
              </button>
            </div>
          </div>
        )}

        {/* Format Selection Step */}
        {step === 'format' && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Choose Export Format</h3>
              <p className="text-sm text-gray-600 mt-1">
                Select how you want to export your user stories to JIRA.
              </p>
            </div>

            <div className="space-y-4">
              {/* CSV Option */}
              <button
                onClick={() => setExportFormat('csv')}
                className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                  exportFormat === 'csv' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${exportFormat === 'csv' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    <Download className={`w-6 h-6 ${exportFormat === 'csv' ? 'text-blue-600' : 'text-gray-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">Download CSV File</span>
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">Recommended</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Download a CSV file formatted for JIRA import. Use JIRA's built-in CSV import feature to create issues.
                    </p>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      <li>• No JIRA credentials required</li>
                      <li>• Review data before importing</li>
                      <li>• Works with any JIRA instance</li>
                    </ul>
                  </div>
                </div>
              </button>

              {/* API Option */}
              <button
                onClick={() => setExportFormat('api')}
                className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                  exportFormat === 'api' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${exportFormat === 'api' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    <Cloud className={`w-6 h-6 ${exportFormat === 'api' ? 'text-blue-600' : 'text-gray-600'}`} />
                  </div>
                  <div className="flex-1">
                    <span className="font-semibold text-gray-900">Direct API Export</span>
                    <p className="text-sm text-gray-600 mt-1">
                      Create issues directly in JIRA using the API. Requires JIRA credentials and connection.
                    </p>
                    <ul className="text-xs text-gray-500 mt-2 space-y-1">
                      <li>• Instant issue creation</li>
                      <li>• Automatic epic linking</li>
                      <li>• Requires API token</li>
                    </ul>
                  </div>
                </div>
              </button>
            </div>

            {/* CSV-specific options */}
            {exportFormat === 'csv' && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg space-y-4">
                <h4 className="font-medium text-gray-900">CSV Options</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">File Name Prefix</label>
                    <input
                      type="text"
                      value={config.csvFilenamePrefix}
                      onChange={(e) => setConfig({ ...config, csvFilenamePrefix: e.target.value })}
                      placeholder="velocity-export"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Date will be appended automatically</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">JIRA Instance Type</label>
                    <select
                      value={config.bulletStyle}
                      onChange={(e) => setConfig({ ...config, bulletStyle: e.target.value as 'markdown' | 'wiki' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="markdown">JIRA Cloud (Markdown)</option>
                      <option value="wiki">JIRA Server/DC (Wiki)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Affects bullet point formatting</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Story Issue Type</label>
                    <input
                      type="text"
                      value={config.issueType}
                      onChange={(e) => setConfig({ ...config, issueType: e.target.value })}
                      placeholder="Story"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Epic Issue Type</label>
                    <input
                      type="text"
                      value={config.epicIssueType}
                      onChange={(e) => setConfig({ ...config, epicIssueType: e.target.value })}
                      placeholder="Epic"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>
                </div>

                {/* Additional options */}
                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <h5 className="text-sm font-medium text-gray-700">Additional Options</h5>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="normalizeUnicode"
                      checked={config.normalizeUnicode}
                      onChange={(e) => setConfig({ ...config, normalizeUnicode: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="normalizeUnicode" className="text-sm text-gray-700">
                      Normalize Unicode characters
                      <span className="block text-xs text-gray-500">Replace em-dashes and smart quotes with ASCII equivalents (for older JIRA instances)</span>
                    </label>
                  </div>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="includePriority"
                      checked={config.includePriority}
                      onChange={(e) => setConfig({ ...config, includePriority: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-1"
                    />
                    <div className="flex-1">
                      <label htmlFor="includePriority" className="text-sm text-gray-700">
                        Include Priority column
                      </label>
                      {config.includePriority && (
                        <div className="mt-2">
                          <label className="block text-xs text-gray-500 mb-1">Default Priority</label>
                          <select
                            value={config.defaultPriority}
                            onChange={(e) => setConfig({ ...config, defaultPriority: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Highest">Highest</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                            <option value="Lowest">Lowest</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Export Summary</h4>
              <p className="text-sm text-blue-800">
                {selectedEpics.size} epic{selectedEpics.size !== 1 ? 's' : ''} and {selectedStories.size} stor{selectedStories.size !== 1 ? 'ies' : 'y'} will be exported.
              </p>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep('select')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              {exportFormat === 'csv' ? (
                <button
                  onClick={performCsvExport}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download CSV
                </button>
              ) : (
                <button
                  onClick={() => setStep('config')}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Configure JIRA Connection
                </button>
              )}
            </div>
          </div>
        )}

        {/* Config Step */}
        {step === 'config' && (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">JIRA Configuration</h3>
              <p className="text-sm text-gray-600">
                {isConfigured ? 'Using saved JIRA configuration. Modify if needed.' : 'Enter your JIRA credentials.'}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">JIRA URL</label>
                <input
                  type="text"
                  value={config.url}
                  onChange={(e) => setConfig({ ...config, url: e.target.value })}
                  placeholder="https://your-company.atlassian.net"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username/Email</label>
                  <input
                    type="text"
                    value={config.username}
                    onChange={(e) => setConfig({ ...config, username: e.target.value })}
                    placeholder="your-email@company.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API Token</label>
                  <input
                    type="password"
                    value={config.apiToken}
                    onChange={(e) => setConfig({ ...config, apiToken: e.target.value })}
                    placeholder="API token"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Key</label>
                  <input
                    type="text"
                    value={config.projectKey}
                    onChange={(e) => setConfig({ ...config, projectKey: e.target.value })}
                    placeholder="PROJ"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Story Issue Type</label>
                  <input
                    type="text"
                    value={config.issueType}
                    onChange={(e) => setConfig({ ...config, issueType: e.target.value })}
                    placeholder="Story"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Epic Issue Type</label>
                  <input
                    type="text"
                    value={config.epicIssueType}
                    onChange={(e) => setConfig({ ...config, epicIssueType: e.target.value })}
                    placeholder="Epic"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Connection status */}
              {connectionStatus && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${
                  connectionStatus.success 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  {connectionStatus.success ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="text-sm text-green-900">Connection successful!</p>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <p className="text-sm text-red-900">{connectionStatus.error}</p>
                    </>
                  )}
                </div>
              )}

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Export Summary</h4>
                <p className="text-sm text-gray-600">
                  {selectedEpics.size} epic{selectedEpics.size !== 1 ? 's' : ''} and {selectedStories.size} stor{selectedStories.size !== 1 ? 'ies' : 'y'} will be exported to JIRA.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep('format')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={testConnection}
                disabled={testingConnection || !config.url || !config.username || !config.apiToken || !config.projectKey}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {testingConnection && <Loader className="w-4 h-4 animate-spin" />}
                Test Connection
              </button>
              <button
                onClick={performApiExport}
                disabled={exporting || !connectionStatus?.success}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {exporting && <Loader className="w-4 h-4 animate-spin" />}
                Export to JIRA
              </button>
            </div>
          </div>
        )}

        {/* Exporting Step */}
        {step === 'exporting' && (
          <div className="p-6 text-center">
            <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Exporting to JIRA...</p>
            <p className="text-sm text-gray-600 mt-2">Creating and updating issues in JIRA</p>
          </div>
        )}

        {/* Complete Step - CSV Export */}
        {step === 'complete' && exportFormat === 'csv' && csvExportResult && (
          <div className="p-6">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">CSV Export Complete!</h3>
              <p className="text-gray-600">
                Your file has been downloaded: <span className="font-medium">{csvExportResult.filename}</span>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {csvExportResult.epicCount}
                </p>
                <p className="text-sm text-purple-900">Epics</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {csvExportResult.storyCount}
                </p>
                <p className="text-sm text-blue-900">User Stories</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-amber-900 mb-2">Next Steps: Import into JIRA</h4>
              <ol className="text-sm text-amber-800 space-y-2 list-decimal list-inside">
                <li>Open your JIRA project</li>
                <li>Go to <strong>Project Settings</strong> → <strong>Import issues</strong></li>
                <li>Select <strong>CSV</strong> as the source</li>
                <li>Upload the downloaded file: <code className="bg-amber-100 px-1 rounded">{csvExportResult.filename}</code></li>
                <li>Map the columns to JIRA fields (Summary, Issue Type, Description, etc.)</li>
                <li>Complete the import wizard</li>
              </ol>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => performCsvExport()}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Again
              </button>
              <button
                onClick={handleComplete}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Complete Step - API Export */}
        {step === 'complete' && exportFormat === 'api' && exportResult && (
          <div className="p-6">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Export Complete!</h3>
              <p className="text-gray-600">
                Successfully exported {exportResult.successful.length} of {exportResult.successful.length + exportResult.failed.length} items
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">
                  {exportResult.successful.filter(r => r.action === 'created').length}
                </p>
                <p className="text-sm text-green-900">Created</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {exportResult.successful.filter(r => r.action === 'updated').length}
                </p>
                <p className="text-sm text-blue-900">Updated</p>
              </div>
            </div>

            {exportResult.failed.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Failed ({exportResult.failed.length}):</h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-1">
                    {exportResult.failed.map((f, index) => (
                      <li key={index}>• {f.item.id}: {f.error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {exportResult.successful.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Created Issues:</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <ul className="text-sm text-gray-700 space-y-1">
                    {exportResult.successful.map((s, index) => (
                      <li key={index}>
                        <span className="font-medium">{s.key}</span> - {s.item.title || s.item.id} ({s.action})
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <button
              onClick={handleComplete}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

